import { useState, useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";

import { useAuth } from "../../contexts/auth";
import { useSearch } from "../../contexts/search";
import { getPoints, getAlarms } from "../../services/api";
import {
  sendLocalNotification,
  areNotificationsEnabled,
  shouldUseFCM,
} from "../../services/notifications";
import { fetchToken } from "../../firebase";

import Card from "./Card";

const CardList = ({ setLoading, setIsSlowConnection }) => {
  const { logout, setLoginFailed, setLoginFailedMessage, user } = useAuth();
  const { search } = useSearch();

  const [points, setPoints] = useState([]);
  const [filteredPoints, setFilteredPoints] = useState([]);
  const [previousAlarms, setPreviousAlarms] = useState({});
  const intervalRef = useRef(null);

  useEffect(() => {
    handleGetPoints();
    intervalRef.current = setInterval(handleGetPoints, 1000 * 60);
    return () => clearInterval(intervalRef.current);
  }, []);

  // Configurar FCM para dispositivos móveis
  useEffect(() => {
    const setupFCM = async () => {
      if (shouldUseFCM() && user?.id) {
        await fetchToken(user.id);
      }
    };
    setupFCM();
  }, [user]);

  const handleGetPoints = async () => {
    try {
      const response = await getPoints();
      const errorMessage = ["Expired token", "Connection timed out"];

      if (!response || errorMessage.includes(response.data)) {
        console.log("response error");
        setLoginFailed(true);
        setLoginFailedMessage(response.data);
        logout();
      }

      if (typeof response.data === "string" && response.data.includes("SQLSTATE[HY000] [2002]")) {
        setLoading(false);
        setIsSlowConnection(true);
        setLoading(true);
        return;
      }

      if (!Array.isArray(response.data)) {
        return;
      }

      // Remover duplicatas usando Map
      const uniquePoints = Array.from(
        new Map(response.data.map((point) => [point.pointId, point])).values()
      );

      // Verificar novos alarmes
      if (areNotificationsEnabled()) {
        uniquePoints.forEach((point) => {
          if (
            point.alarm &&
            (!previousAlarms[point.pointId] ||
              previousAlarms[point.pointId] !== point.alarm)
          ) {
            // Se for dispositivo móvel, o FCM cuidará das notificações
            if (!shouldUseFCM()) {
              sendLocalNotification(`Alarme: ${point.name}`, {
                body: point.alarm,
                tag: `alarm-${point.pointId}`,
                data: { pointId: point.pointId },
              });
            }
          }
        });
      }

      // Atualizar estado dos alarmes anteriores
      const newPreviousAlarms = {};
      uniquePoints.forEach((point) => {
        if (point.alarm) {
          newPreviousAlarms[point.pointId] = point.alarm;
        }
      });
      setPreviousAlarms(newPreviousAlarms);

      setPoints(uniquePoints);
      setLoading(false);
      await getAlarms();
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleApiError = (error) => {
    if (error.response && error.response.status === 401) {
      const reloadCount = parseInt(localStorage.getItem("reloadCount") || "0", 10);
      
      if (reloadCount >= 3) {
        console.error("Muitas tentativas de reload falharam. Deslogando...");
        localStorage.setItem("reloadCount", "0");
        logout();
      } else {
        localStorage.setItem("reloadCount", reloadCount + 1);
        console.log(`401 detectado. Tentativa de reload ${reloadCount + 1}/3 em 2s...`);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } else {
      localStorage.setItem("reloadCount", "0");
      console.error("Erro na API:", error);
    }
  };

  useEffect(() => {
    handleFilterPoints();
  }, [search, points]);

  const handleFilterPoints = () => {
    const upperSearch = search.toUpperCase();
    try {
      setFilteredPoints(
        points?.filter((point) => point.name.includes(upperSearch))
      );
    } catch (error) {
      handleFilterError(error);
    }
  };

  const handleFilterError = (error) => {
    //console.log(error);
    window.location.href = "/";
  };

  return (
    <Container>
      <Row className="justify-content-center mb-5">
        <Col md={7} lg={5}>
          {filteredPoints?.map((point) => (
            <Card point={point} key={point.pointId} />
          ))}
        </Col>
      </Row>
    </Container>
  );
};

export default CardList;
